import { getErrorMessage } from '@/lib/sync/error-utils';

export interface Operation<T> {
  id: string;
  type: string;
  execute: () => Promise<T>;
  optimisticUpdate: () => void;
  rollback: () => void | Promise<void>;
  priority?: number;
  dedupKey?: string;
}

interface QueueEntry<T> {
  operation: Operation<T>;
  status: 'pending' | 'in-flight' | 'scheduled';
  attempts: number;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
  promise: Promise<T>;
  scheduledTimeout?: ReturnType<typeof setTimeout>;
}

type OperationQueueOptions = {
  maxConcurrent?: number;
  maxAttempts?: number;
  baseBackoffMs?: number;
};

const DEFAULT_OPTIONS: Required<OperationQueueOptions> = {
  maxConcurrent: 3,
  maxAttempts: 3,
  baseBackoffMs: 100,
};

const OPERATION_LIMITS: Record<string, Partial<OperationQueueOptions>> = {
  addScore: { maxAttempts: 3, baseBackoffMs: 100, maxConcurrent: 3 },
  setScore: { maxAttempts: 3, baseBackoffMs: 100, maxConcurrent: 3 },
  startRound: { maxAttempts: 2, baseBackoffMs: 200, maxConcurrent: 2 },
  completeRound: { maxAttempts: 2, baseBackoffMs: 200, maxConcurrent: 2 },
  pauseSession: { maxAttempts: 2, baseBackoffMs: 300, maxConcurrent: 2 },
  resumeSession: { maxAttempts: 2, baseBackoffMs: 300, maxConcurrent: 2 },
  addRoundNote: { maxAttempts: 2, baseBackoffMs: 150, maxConcurrent: 3 },
  removeRoundNote: { maxAttempts: 2, baseBackoffMs: 150, maxConcurrent: 3 },
  addRoundTag: { maxAttempts: 2, baseBackoffMs: 150, maxConcurrent: 3 },
  removeRoundTag: { maxAttempts: 2, baseBackoffMs: 150, maxConcurrent: 3 },
  joinPlayer: { maxAttempts: 2, baseBackoffMs: 200, maxConcurrent: 2 },
  removePlayer: { maxAttempts: 2, baseBackoffMs: 200, maxConcurrent: 2 },
  updateSessionName: { maxAttempts: 2, baseBackoffMs: 200, maxConcurrent: 2 },
  updateSessionNotes: { maxAttempts: 2, baseBackoffMs: 200, maxConcurrent: 2 },
  addSessionTag: { maxAttempts: 2, baseBackoffMs: 150, maxConcurrent: 2 },
  removeSessionTag: { maxAttempts: 2, baseBackoffMs: 150, maxConcurrent: 2 },
  updateSessionGameConfig: { maxAttempts: 2, baseBackoffMs: 250, maxConcurrent: 2 },
  endSession: { maxAttempts: 1, baseBackoffMs: 500, maxConcurrent: 1 },
  setSessionWinner: { maxAttempts: 2, baseBackoffMs: 200, maxConcurrent: 1 },
  deleteSession: { maxAttempts: 1, baseBackoffMs: 500, maxConcurrent: 1 },
  undo: { maxAttempts: 2, baseBackoffMs: 200, maxConcurrent: 1 },
  redo: { maxAttempts: 2, baseBackoffMs: 200, maxConcurrent: 1 },
};

const resolveLimits = (
  operationType: string,
  base: Required<OperationQueueOptions>,
): Required<OperationQueueOptions> => {
  const overrides = OPERATION_LIMITS[operationType] ?? {};
  return {
    maxConcurrent: overrides.maxConcurrent ?? base.maxConcurrent,
    maxAttempts: overrides.maxAttempts ?? base.maxAttempts,
    baseBackoffMs: overrides.baseBackoffMs ?? base.baseBackoffMs,
  };
};

export interface OperationQueueDiagnostics {
  pending: number;
  inFlight: number;
  scheduled: number;
  total: number;
  pendingByType: Record<string, number>;
  inFlightByType: Record<string, number>;
  lastFailure?: {
    operationType: string;
    attempts: number;
    errorMessage: string;
    timestamp: number;
  };
}

type DiagnosticsListener = (diagnostics: OperationQueueDiagnostics) => void;

export class OperationQueue {
  private readonly entries = new Map<string, QueueEntry<unknown>>();
  private readonly order: string[] = [];
  private readonly inFlight = new Set<string>();
  private readonly inFlightByType = new Map<string, number>();
  private processing = false;
  private readonly options: Required<OperationQueueOptions>;
  private diagnosticsListener?: DiagnosticsListener;
  private lastFailure?: OperationQueueDiagnostics['lastFailure'];

  constructor(options: OperationQueueOptions = {}) {
    this.options = {
      maxConcurrent: options.maxConcurrent ?? DEFAULT_OPTIONS.maxConcurrent,
      maxAttempts: options.maxAttempts ?? DEFAULT_OPTIONS.maxAttempts,
      baseBackoffMs: options.baseBackoffMs ?? DEFAULT_OPTIONS.baseBackoffMs,
    };
  }

  setDiagnosticsListener(listener?: DiagnosticsListener): void {
    this.diagnosticsListener = listener;
    if (listener) {
      queueMicrotask(() => {
        if (this.diagnosticsListener === listener) {
          listener(this.getDiagnostics());
        }
      });
    }
  }

  getDiagnostics(): OperationQueueDiagnostics {
    const diagnostics = this.computeDiagnostics();
    return diagnostics;
  }

  enqueue<T>(operation: Operation<T>): Promise<T> {
    const key = operation.dedupKey ?? operation.id;
    const existing = this.entries.get(key) as QueueEntry<T> | undefined;
    if (existing) {
      return existing.promise;
    }

    let resolvePromise!: (value: T) => void;
    let rejectPromise!: (reason: unknown) => void;
    const promise = new Promise<T>((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });

    const entry: QueueEntry<T> = {
      operation,
      status: 'pending',
      attempts: 0,
      resolve: resolvePromise,
      reject: rejectPromise,
      promise,
    };

    this.entries.set(key, entry as QueueEntry<unknown>);
    this.order.push(key);

    try {
      operation.optimisticUpdate();
    } catch (error) {
      this.entries.delete(key);
      this.removeFromOrder(key);
      rejectPromise(error);
      throw error;
    }

    this.scheduleProcessing();
    this.emitDiagnostics();
    return promise;
  }

  private scheduleProcessing(): void {
    if (this.processing) {
      return;
    }
    this.processing = true;
    queueMicrotask(() => {
      this.processing = false;
      this.processQueue();
    });
  }

  private processQueue(): void {
    while (this.inFlight.size < this.options.maxConcurrent) {
      const key = this.nextRunnableKey();
      if (!key) {
        break;
      }
      const entry = this.entries.get(key);
      if (!entry || entry.status !== 'pending') {
        continue;
      }
      this.runOperation(key, entry).catch((error) => {
        console.error('[OperationQueue] Unexpected failure', error);
      });
    }
  }

  private nextRunnableKey(): string | undefined {
    for (const key of this.order) {
      const entry = this.entries.get(key);
      if (!entry || entry.status !== 'pending') {
        continue;
      }
      const limits = resolveLimits(entry.operation.type, this.options);
      const inFlightForType = this.inFlightByType.get(entry.operation.type) ?? 0;
      if (inFlightForType < limits.maxConcurrent) {
        return key;
      }
    }
    return undefined;
  }

  private incrementTypeCounter(operationType: string): void {
    const current = this.inFlightByType.get(operationType) ?? 0;
    this.inFlightByType.set(operationType, current + 1);
  }

  private decrementTypeCounter(operationType: string): void {
    const current = this.inFlightByType.get(operationType);
    if (current === undefined) {
      return;
    }
    if (current <= 1) {
      this.inFlightByType.delete(operationType);
    } else {
      this.inFlightByType.set(operationType, current - 1);
    }
  }

  private async runOperation(key: string, entry: QueueEntry<unknown>): Promise<void> {
    this.inFlight.add(key);
    entry.status = 'in-flight';
    entry.attempts += 1;
    const { type } = entry.operation;
    this.incrementTypeCounter(type);

    try {
      const result = await entry.operation.execute();
      this.entries.delete(key);
      this.removeFromOrder(key);
      entry.resolve(result);
      this.lastFailure = undefined;
    } catch (error) {
      await this.handleFailure(key, entry, error);
      return;
    } finally {
      this.inFlight.delete(key);
      this.decrementTypeCounter(type);
      if (this.hasMoreWork()) {
        this.scheduleProcessing();
      }
      this.emitDiagnostics();
    }
  }

  private async handleFailure(
    key: string,
    entry: QueueEntry<unknown>,
    error: unknown,
  ): Promise<void> {
    try {
      await entry.operation.rollback();
    } catch (rollbackError) {
      console.error('[OperationQueue] Rollback failed', rollbackError);
    }

    const mergedLimits = resolveLimits(entry.operation.type, this.options);
    const maxAttempts = mergedLimits.maxAttempts;
    const baseBackoffMs = mergedLimits.baseBackoffMs;

    const errorMessage = getErrorMessage(error);
    this.lastFailure = {
      operationType: entry.operation.type,
      attempts: entry.attempts,
      errorMessage,
      timestamp: Date.now(),
    };

    if (entry.attempts >= maxAttempts) {
      this.entries.delete(key);
      this.removeFromOrder(key);
      entry.reject(error);
      this.emitDiagnostics();
      return;
    }

    entry.status = 'scheduled';
    const delay = baseBackoffMs * Math.pow(2, entry.attempts - 1);
    entry.scheduledTimeout = setTimeout(() => {
      entry.status = 'pending';
      entry.scheduledTimeout = undefined;
      this.scheduleProcessing();
      this.emitDiagnostics();
    }, delay);
    this.emitDiagnostics();
  }

  private hasMoreWork(): boolean {
    return (
      this.order.some((key) => this.entries.get(key)?.status === 'pending') ||
      this.inFlight.size > 0
    );
  }

  private removeFromOrder(key: string): void {
    const index = this.order.indexOf(key);
    if (index !== -1) {
      this.order.splice(index, 1);
    }
  }

  private computeDiagnostics(): OperationQueueDiagnostics {
    let pending = 0;
    let scheduled = 0;
    const pendingByType = new Map<string, number>();
    const inFlightByType = new Map(this.inFlightByType);

    for (const entry of this.entries.values()) {
      if (entry.status === 'pending') {
        pending += 1;
        pendingByType.set(entry.operation.type, (pendingByType.get(entry.operation.type) ?? 0) + 1);
      } else if (entry.status === 'scheduled') {
        scheduled += 1;
      }
    }

    return {
      pending,
      inFlight: this.inFlight.size,
      scheduled,
      total: this.entries.size,
      pendingByType: Object.fromEntries(pendingByType.entries()),
      inFlightByType: Object.fromEntries(inFlightByType.entries()),
      lastFailure: this.lastFailure,
    };
  }

  private emitDiagnostics(): void {
    if (!this.diagnosticsListener) {
      return;
    }
    this.diagnosticsListener(this.getDiagnostics());
  }
}

const globalObject: typeof globalThis | undefined =
  typeof globalThis === 'object' ? globalThis : undefined;
const cryptoLike = globalObject?.crypto as { randomUUID?: () => string } | undefined;

let fallbackCounter = 0;

export const createOperationId = (): string => {
  if (cryptoLike?.randomUUID) {
    return cryptoLike.randomUUID();
  }
  fallbackCounter += 1;
  return `op-${Date.now().toString(16)}-${fallbackCounter.toString(16)}`;
};
